import React, { createContext, useContext, useState } from 'react';
import ConfirmationPrompt, { Props as PromptProps } from './ConfirmationPrompt';

interface OpenPromptOptions extends Omit<PromptProps, 'open' | 'onClose'> {
  onClose?: VoidFunction;
}

interface _useConfirmationPromptReturn {
  promptData: PromptProps;
  openPrompt: (options: OpenPromptOptions) => void;
  closePrompt: VoidFunction;
}
// eslint-disable-next-line @typescript-eslint/no-empty-function
const NOOP = function () {};
const DEFAULT_STATE = {
  open: false,
  message: '',
  onConfirm: NOOP,
  onClose: NOOP,
};
const _useConfirmationPrompt = (): _useConfirmationPromptReturn => {
  const [promptData, setPromptData] = useState<PromptProps>(DEFAULT_STATE);
  const openPrompt = (options: OpenPromptOptions) => {
    const onClose = () => {
      options.onClose?.();
      closePrompt();
    };
    setPromptData({
      ...options,
      open: true,
      onClose,
    });
  };
  const closePrompt = () => {
    setPromptData(DEFAULT_STATE);
  };
  return {
    openPrompt,
    closePrompt,
    promptData,
  };
};

const ConfirmationPromptContext = createContext<_useConfirmationPromptReturn>({
  promptData: DEFAULT_STATE,
  openPrompt: NOOP,
  closePrompt: NOOP,
});

interface ConfirmationPromptProviderProps {
  children: React.ReactNode;
}

const ConfirmationPromptWrapper = () => {
  const { promptData } = useConfirmationPromptContext();
  return <ConfirmationPrompt {...promptData} />;
};

export const ConfirmationPromptProvider = ({
  children,
}: ConfirmationPromptProviderProps) => {
  const props = _useConfirmationPrompt();
  return (
    <ConfirmationPromptContext.Provider value={props}>
      {children}
      <ConfirmationPromptWrapper />
    </ConfirmationPromptContext.Provider>
  );
};

const useConfirmationPromptContext = () => {
  return useContext(ConfirmationPromptContext);
};
export default useConfirmationPromptContext;
